import { DEFAULT_AUTONOMY, MINUTE, } from './types.js';
const P = (p) => ({
    baseMs: MINUTE,
    factor: 2,
    maxMs: 60 * MINUTE,
    maxAttempts: 3,
    rerouteMachineFrom: 0,
    notify: false,
    ...p,
});
/** Per-failure-class policy. `rate_limited` is special-cased: park until the window resets. */
export const RETRY_POLICIES = {
    rate_limited: P({
        baseMs: 30 * MINUTE,
        maxAttempts: Number.POSITIVE_INFINITY,
        block: 'account',
        autoFix: 'park_until_reset',
    }),
    auth_expired: P({
        baseMs: 0,
        maxAttempts: 2,
        rerouteMachineFrom: 1,
        block: 'machine_agent',
        notify: true,
    }),
    op_locked: P({
        baseMs: 0,
        maxAttempts: 2,
        rerouteMachineFrom: 1,
        block: 'machine',
        notify: true,
    }),
    disk_full: P({
        baseMs: 0,
        maxAttempts: 2,
        rerouteMachineFrom: 1,
        block: 'machine',
        notify: true,
        autoFix: 'prune_worktrees',
    }),
    tcc_denied: P({ maxAttempts: 1, notify: true }),
    permission_denied: P({ maxAttempts: 2, notify: true }),
    sandbox_denied: P({ maxAttempts: 2, notify: true }),
    file_locked: P({
        baseMs: 30_000,
        maxMs: 10 * MINUTE,
        maxAttempts: 6,
        autoFix: 'clear_lock_and_retry',
    }),
    ci_credits: P({ baseMs: 0, maxAttempts: 2, autoFix: 'run_tests_locally' }),
    no_output: P({
        baseMs: 2 * MINUTE,
        maxAttempts: 3,
        rerouteMachineFrom: 2,
        autoFix: 'nudge_or_restart',
    }),
    test_failure: P({ maxAttempts: 3 }),
    crash: P({ maxAttempts: 5, rerouteMachineFrom: 2, autoFix: 'retry_with_backoff' }),
    unknown: P({ baseMs: 2 * MINUTE, maxAttempts: 3, notify: true }),
};
/** FNV-1a → [0,1): deterministic jitter so retries of many tasks don't align. */
function hash01(s) {
    let h = 0x811c9dc5;
    for (let i = 0; i < s.length; i++) {
        h ^= s.charCodeAt(i);
        h = Math.imul(h, 0x01000193);
    }
    return (h >>> 0) / 2 ** 32;
}
export function backoffDelay(policy, attempt, jitter = 0, key = '') {
    const raw = Math.min(policy.maxMs, policy.baseMs * policy.factor ** Math.max(0, attempt - 1));
    return Math.round(raw * (1 + jitter * hash01(`${key}:${attempt}`)));
}
const iso = (t) => new Date(t).toISOString();
/** Retry / park / reroute decision for a failed or stalled run. Pure. */
export function decideRetry(f) {
    const policy = { ...RETRY_POLICIES[f.cause], ...f.policies?.[f.cause] };
    const autonomy = { ...DEFAULT_AUTONOMY, ...f.autonomy };
    const blocks = [];
    const avoid = [];
    if (f.cause === 'rate_limited') {
        const until = f.resetsAt ?? f.now + policy.baseMs;
        blocks.push({
            scope: 'account',
            accountId: f.accountId ?? f.agent,
            agent: f.agent,
            until,
            cause: f.cause,
            reason: `rate limited until ${iso(until)}`,
        });
        const d = {
            action: 'park',
            notBefore: until,
            avoid: [],
            blocks,
            notify: false,
            autoFix: 'park_until_reset',
            reason: `rate limited: park until ${iso(until)}`,
        };
        if (!f.agentLocked) {
            const reroute = { avoid: [{ agent: f.agent }], notBefore: f.now };
            if (autonomy.autoSwitchAgent) {
                d.action = 'retry';
                d.avoid = reroute.avoid;
                d.notBefore = f.now;
                d.reason = `rate limited until ${iso(until)}: rerouting to the other agent`;
            }
            else {
                d.offer = { ...reroute, reason: 'switch agent now instead of waiting for the reset' };
            }
        }
        return d;
    }
    if (policy.block === 'machine') {
        blocks.push({ scope: 'machine', machineId: f.machineId, cause: f.cause, reason: f.cause });
    }
    else if (policy.block === 'machine_agent') {
        blocks.push({
            scope: 'machine_agent',
            machineId: f.machineId,
            agent: f.agent,
            cause: f.cause,
            reason: f.cause,
        });
    }
    else if (policy.block === 'account') {
        blocks.push({
            scope: 'account',
            accountId: f.accountId ?? f.agent,
            agent: f.agent,
            cause: f.cause,
            reason: f.cause,
        });
    }
    if (f.attempt >= policy.maxAttempts) {
        const d = {
            action: 'escalate',
            avoid,
            blocks,
            notify: true,
            reason: `${f.cause}: ${f.attempt} failure(s), max ${policy.maxAttempts}; needs you`,
        };
        if (policy.autoFix)
            d.autoFix = policy.autoFix;
        return d;
    }
    const delay = backoffDelay(policy, f.attempt, f.jitter ?? 0, f.taskId);
    const reroute = policy.rerouteMachineFrom > 0 && f.attempt >= policy.rerouteMachineFrom;
    const rerouteAvoid = policy.block === 'machine_agent'
        ? { machineId: f.machineId, agent: f.agent }
        : { machineId: f.machineId };
    if (reroute && autonomy.autoRerouteMachine)
        avoid.push(rerouteAvoid);
    const retries = Number.isFinite(policy.maxAttempts)
        ? `${f.attempt}/${policy.maxAttempts - 1}`
        : `${f.attempt}`;
    const d = {
        action: 'retry',
        notBefore: f.now + delay,
        avoid,
        blocks,
        notify: policy.notify,
        reason: `${f.cause}: retry ${retries} in ${Math.round(delay / 1000)}s${avoid.length ? ' on another machine' : ''}`,
    };
    if (reroute && !autonomy.autoRerouteMachine) {
        d.offer = {
            avoid: [rerouteAvoid],
            notBefore: f.now + delay,
            reason: 'retry on another machine',
        };
    }
    if (policy.autoFix)
        d.autoFix = policy.autoFix;
    return d;
}
/** Apply a decision to the task (backoff + avoid list) for the next router pass. */
export function applyRetry(task, d) {
    const next = { ...task, avoid: [...(task.avoid ?? []), ...d.avoid] };
    if (d.notBefore !== undefined)
        next.notBefore = d.notBefore;
    return next;
}
/** Merge new blocks into the active set, dropping expired ones and exact duplicates. */
export function mergeBlocks(current, added, now) {
    const key = (b) => `${b.scope}|${b.machineId ?? ''}|${b.agent ?? ''}|${b.accountId ?? ''}`;
    const out = new Map();
    for (const b of [...current, ...added]) {
        if (b.until !== undefined && b.until <= now)
            continue;
        out.set(key(b), b);
    }
    return [...out.values()];
}
/** Clear blocks once the fix is confirmed (e.g. doctor passes, login refreshed). */
export function clearBlocks(current, match) {
    return current.filter((b) => !Object.entries(match).every(([k, v]) => b[k] === v));
}
//# sourceMappingURL=retry.js.map